import SkeletonType2 from "@/app/components/ui/Skeletons/Type2";
import WhatsappTemplatePreviewModal from "@/app/components/ui/WhatsappTemplatePreviewModal";
import { useClinicLocationScriptAPI } from "@/app/hooks/api_hooks/bookings/useClinicLocationScriptAPI";
import { useSendClinicLocationWhatsappAPI } from "@/app/hooks/api_hooks/bookings/useSendClinicLocationWhatsappAPI";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

interface IClinicLocationWhatsappProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  clinicId: string;
  clinicName?: string;
}

const ClinicLocationWhatsapp = ({ open, setOpen, clinicId, clinicName }: IClinicLocationWhatsappProps) => {
  const { execute: fetchClinicScript, clinicScriptDetails } = useClinicLocationScriptAPI();
  const { execute: sendClinicLocation } = useSendClinicLocationWhatsappAPI();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("CUSTOMER_PROFILE", "id");
  const { routeState } = useRouteInfo(routeId);
  const { customerDetails } = routeState?.state as ICustomerProfileState;
  const { execute: logAPI } = useClickLogAPI();
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);

  const [loader, setLoader] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  useEffect(() => {
    if (open && clinicId) {
      setLoader(true);
      fetchClinicScript(clinicId).then(() => {
        setLoader(false);
      });
    }
  }, [open, clinicId, fetchClinicScript]);

  const handleSendClinicLocation = (mobile: string) => {
    setIsSending(true);
    sendClinicLocation(clinicId, mobile).then(({ status }) => {
      setIsSending(false);
      const logBody: IClickLogBody = {
        event_type: status ? "WHATSAPP_CLINIC_LOCATION_SENT" : "WHATSAPP_CLINIC_LOCATION_SEND_FAILED",
        mobile: mobile,
        clinic_id: clinicId,
        clinic_name: clinicName,
        vms_user_id: userLogInfo,
        scr_id: "CLINIC_LOCATION_WHATSAPP_MODAL",
        timestamp: Date.now(),
      };
      logAPI(logBody);
      if (status) {
        setOpen(false);
      }
    });
  };

  const handleClose = () => {
    logAPI({
      event_type: "WHATSAPP_CLINIC_LOCATION_MODAL_CLOSED",
      clinic_id: clinicId,
      clinic_name: clinicName,
      vms_user_id: userLogInfo,
      scr_id: "CLINIC_LOCATION_WHATSAPP_MODAL",
      timestamp: Date.now(),
    });
    setOpen(false);
  };

  if (loader) {
    return (
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="z-50 fixed inset-0 overflow-y-auto" onClose={() => setOpen(false)}>
          <div className="sm:block flex justify-center items-end px-4 sm:p-0 pt-4 pb-20 min-h-screen text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span className="sm:inline-block hidden sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white shadow-xl sm:my-8 p-[16px] rounded-[12px] max-w-[516px] w-full text-left transform transition-all sm:align-middle">
                <SkeletonType2 />
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  return (
    <WhatsappTemplatePreviewModal
      open={open}
      onClose={handleClose}
      title="Share Clinic Location on WhatsApp"
      initialMobile={customerDetails?.mobile ?? ""}
      script={clinicScriptDetails?.whatsapp_text ?? ""}
      fullAddress={clinicScriptDetails?.address}
      nearbyLandmarks={clinicScriptDetails?.nearby_landmarks}
      isSending={isSending}
      sendButtonLabel="Share Location"
      onSend={handleSendClinicLocation}
      onCancel={handleClose}
    />
  );
};

export default ClinicLocationWhatsapp;
